import React from 'react';

import { Icon, Flex, Modal, Button } from 'design-system-web';
import { useTranslation } from 'react-i18next';

import { ICON_SET_FONTAWESOME } from 'lane-shared/helpers/constants/icons';

import { P } from 'components/typography';

import type { CustomField } from 'domains/visitorManagement/types/CustomField';

import styles from './CustomFieldsModal.scss';

type CustomFieldsModalProps = {
  isOpen: boolean;
  customFields?: CustomField[];
  onClose: () => void;
  onAdd: (selectedFields: CustomField[]) => void;
};

export function CustomFieldsModal({
  isOpen,
  customFields = [],
  onClose,
  onAdd,
}: CustomFieldsModalProps) {
  const { t } = useTranslation();

  const hasFields = customFields.length > 0;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      testId="customFieldsModal"
      className={styles.modal}
      size="small"
      includeHeader={false}
      disableContentPadding
      actions={
        <>
          <Button
            variant="secondary"
            onClick={onClose}
            testId="customFieldsModalCancel"
          >
            {t(
              'web.admin.content.features.visitor.management.general.customFields.cancel'
            )}
          </Button>
          <Button
            variant="primary"
            onClick={() => onAdd([])}
            testId="customFieldsModalAdd"
          >
            {t(
              'web.admin.content.features.visitor.management.general.customFields.add'
            )}
          </Button>
        </>
      }
    >
      <div className={styles.header}>
        <span className={styles.headerTitle}>
          {t(
            'web.admin.content.features.visitor.management.general.customFields.title'
          )}
        </span>
        <button
          type="button"
          className={styles.closeButton}
          onClick={onClose}
          data-test="customFieldsModalClose"
          aria-label={t(
            'web.admin.content.features.visitor.management.general.customFields.close'
          )}
        >
          <span className={styles.redDot} />
        </button>
      </div>

      <div className={styles.body}>
        <P className={styles.searchLabel}>
          {t(
            'web.admin.content.features.visitor.management.general.customFields.search'
          )}
        </P>
        <div className={styles.searchInputWrapper}>
          <Icon
            name="search"
            set={ICON_SET_FONTAWESOME}
            className={styles.searchIcon}
          />
          <input
            type="text"
            className={styles.searchInput}
            placeholder={t(
              'web.admin.content.features.visitor.management.general.customFields.searchPlaceholder'
            )}
            data-test="customFieldsSearchInput"
            disabled
          />
        </div>

        <P className={styles.selectFieldsLabel}>
          {t(
            'web.admin.content.features.visitor.management.general.customFields.selectFields'
          )}
        </P>

        <div
          className={styles.fieldsContainer}
          data-test="customFieldsListContainer"
        >
          {hasFields ? (
            customFields.map(field => (
              <label
                key={field._id}
                className={styles.fieldItem}
                data-test="customFieldItem"
              >
                <input
                  type="checkbox"
                  className={styles.checkbox}
                  disabled
                />
                <span>{field.name}</span>
              </label>
            ))
          ) : (
            <Flex
              direction="column"
              align="center"
              justify="center"
              className={styles.emptyState}
              data-test="customFieldsEmptyState"
            >
              <Icon
                name="list"
                set={ICON_SET_FONTAWESOME}
                size="large"
                className={styles.emptyStateIcon}
              />
              <P className={styles.emptyStateText}>
                {t(
                  'web.admin.content.features.visitor.management.general.customFields.emptyState'
                )}
              </P>
            </Flex>
          )}
        </div>
      </div>
    </Modal>
  );
}
